import { useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, CalendarCheck, CalendarX, Clock, Download, Euro, GraduationCap, UserRound } from "lucide-react";

import {
    DateRangeFilter,
    FILTER_LABEL,
    GranularityToggle,
    HorizontalBarChart,
    ReportChartCard,
    StatTile,
    TimeSeriesChart,
    formatCount,
    formatMoney,
    formatMoneyRounded,
    formatPeriodFull,
    formatPeriodShort,
    toAmount,
    useChartTokens,
    usePagedRows,
    type ReportGranularity,
} from "../../../../shared/components/reports";
import Button from "../../../../shared/ui/button/Button";
import { Pagination } from "../../../../shared/ui/common/Pagination";
import FilterSelect from "../../../../shared/ui/form/FilterSelect";
import Label from "../../../../shared/ui/form/Label";
import { toLocalDateKey } from "../../../../shared/utils/date";
import {
    usePractitionerCourses,
    usePractitionerReport,
    usePractitionerSubjects,
} from "../../application/usePractitionerReport";
import MentorTable from "./MentorTable";
import MaybeLink from "./MaybeLink";
import { useTherapist } from "../../../../context/useTherapist";
import {
    monthOf,
    reportLinks,
    subjectKey,
    subjectName,
    type PractitionerReport,
    type ReportLinks,
    type ReportScope,
} from "../../domain/PractitionerReport";
import MonthStatementDialog from "./MonthStatementDialog";
import SessionDetailTables from "./SessionDetailTables";
import CustomFieldsTable from "./CustomFieldsTable";
import { ModuleCoverageCell, ModuleCoverageHeading } from "./ModuleCoverage";
import { hasModuleCoverage } from "../../domain/PractitionerReport";
import SubjectTypeFilter, { type SubjectTypeOption } from "./SubjectTypeFilter";
import { offeredSubjectKinds, useSessionPreferences } from "../../../center";

const DEFAULT_GRANULARITY: ReportGranularity = "day";

/** The current month, first day to last, as the report opens on. */
function currentMonth(): { from: string; to: string } {
    const today = new Date();
    const first = new Date(today.getFullYear(), today.getMonth(), 1);
    const last = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return { from: toLocalDateKey(first), to: toLocalDateKey(last) };
}

interface Props {
    scope: ReportScope;
    title: string;
    subtitle: string;
    /** Above the filters, beside the title — a way back, for an administrator. */
    headerAction?: ReactNode;
    /**
     * `"<kind>:<id>"` when the page is about one patient or student: the
     * report and its downloads stay on them, and there is no picker.
     */
    fixedSubject?: string;
}

/**
 * A practitioner's sessions over a window: how many, how they ended, with
 * whom, and — when rates apply — what they add up to. The same view serves
 * the practitioner's own page and an administrator's look at a member.
 *
 * Money appears only when a rate applies to something in the window; a
 * centre that never set rates sees no euros at all, rather than zeroes.
 */
export default function PractitionerReportView({ scope, title, subtitle, headerAction, fixedSubject }: Props) {
    const { t, i18n } = useTranslation();
    const locale = i18n.language;
    const tokens = useChartTokens();

    const [{ from, to }, setRange] = useState(currentMonth);
    const [granularity, setGranularity] = useState<ReportGranularity>(DEFAULT_GRANULARITY);
    const [pickedSubject, setSubject] = useState("");
    const [course, setCourse] = useState("");
    const [downloadOpen, setDownloadOpen] = useState(false);
    const subject = fixedSubject ?? pickedSubject;

    const filters = useMemo(
        () => ({ from, to, granularity, subject, course }),
        [from, to, granularity, subject, course]
    );
    const { report, loading, error, tooManyPeriods, retry } = usePractitionerReport(scope, filters);
    const { subjects } = usePractitionerSubjects(scope, from, to, !fixedSubject);
    // Only a training centre's mentors have courses to pick from; for
    // everybody else the list is empty and the picker is not drawn.
    const { courses } = usePractitionerCourses(scope, from, to, fixedSubject ?? "");

    const { therapist } = useTherapist();
    const links = useMemo(
        () => reportLinks(scope, therapist?.id, fixedSubject),
        [scope, therapist?.id, fixedSubject]
    );
    const groupOf = (count: number) => t("reports.groupOf", { count });
    const subjectOptions = subjects.map((option) => ({
        value: subjectKey(option.type, option.id),
        label: subjectName(option, groupOf),
    }));

    const reset = () => {
        setRange(currentMonth());
        setGranularity(DEFAULT_GRANULARITY);
        setSubject("");
        setCourse("");
    };

    return (
        <div className="space-y-6">
            <section className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
                <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                        {headerAction}
                        <h1 className="text-lg font-semibold text-content-primary">{title}</h1>
                        <p className="mt-1 text-sm text-content-secondary">{subtitle}</p>
                    </div>
                    <Button
                        size="sm"
                        variant="outline"
                        startIcon={<Download className="h-4 w-4" aria-hidden="true" />}
                        onClick={() => setDownloadOpen(true)}
                    >
                        {t("reports.download.button")}
                    </Button>
                </div>

                <DateRangeFilter
                    idPrefix="practitioner-report"
                    from={from}
                    to={to}
                    onFromChange={(day) => setRange((range) => ({ ...range, from: day }))}
                    onToChange={(day) => setRange((range) => ({ ...range, to: day }))}
                    onClear={reset}
                    secondRow={<GranularityToggle value={granularity} onChange={setGranularity} />}
                >
                    {!fixedSubject && (
                        <div className="space-y-1.5">
                            <Label className={FILTER_LABEL}>{t("reports.patient")}</Label>
                            <FilterSelect
                                options={subjectOptions}
                                value={subject}
                                onChange={(value) => setSubject(value as string)}
                                placeholder={t("reports.everyone")}
                                searchPlaceholder={t("reports.searchPatient")}
                                icon={<UserRound className="h-5 w-5" />}
                                className="text-sm"
                            />
                        </div>
                    )}
                    {courses.length > 0 && (
                        <div className="space-y-1.5">
                            <Label className={FILTER_LABEL}>{t("reports.course")}</Label>
                            <FilterSelect
                                options={courses.map((option) => ({ value: option.id, label: option.name }))}
                                value={course}
                                onChange={(value) => setCourse(value as string)}
                                placeholder={t("reports.allCourses")}
                                searchPlaceholder={t("reports.searchCourse")}
                                icon={<GraduationCap className="h-5 w-5" />}
                                className="text-sm"
                            />
                        </div>
                    )}
                </DateRangeFilter>

                {tooManyPeriods && (
                    <div
                        role="status"
                        className="flex flex-col gap-3 rounded-xl border border-status-warning/30 bg-status-warning-tint p-4 text-sm text-status-warning-on-tint sm:flex-row sm:items-center sm:justify-between"
                    >
                        <span className="flex items-center gap-2">
                            <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
                            {t("center.reports.tooManyPeriods")}
                        </span>
                        <Button size="sm" variant="outline" onClick={() => setGranularity("month")}>
                            {t("center.reports.groupByMonth")}
                        </Button>
                    </div>
                )}

                {error && (
                    <div
                        role="alert"
                        className="flex flex-col gap-3 rounded-xl border border-status-critical/30 bg-status-critical-tint p-4 text-sm text-status-critical-on-tint sm:flex-row sm:items-center sm:justify-between"
                    >
                        <span>{t("reports.error")}</span>
                        <Button size="sm" variant="outline" onClick={retry}>
                            {t("reports.retry")}
                        </Button>
                    </div>
                )}

                {loading && (
                    <p role="status" className="text-sm text-content-secondary">
                        {t("common.loading")}
                    </p>
                )}

                {report && <ReportTiles report={report} locale={locale} />}
            </section>

            {report && !tooManyPeriods && (
                <ReportCharts report={report} granularity={granularity} locale={locale} tokens={tokens} />
            )}

            {report && report.mentors.length > 0 && <MentorTable report={report} locale={locale} links={links} />}

            {report && !fixedSubject && (
                <SubjectTable report={report} locale={locale} groupOf={groupOf} links={links} />
            )}

            {report && (report.custom_fields ?? []).length > 0 && (
                <CustomFieldsTable
                    fields={report.custom_fields ?? []}
                    subjects={report.subjects}
                    locale={locale}
                    groupOf={groupOf}
                    links={links}
                />
            )}

            {report && <SessionDetailTables report={report} locale={locale} groupOf={groupOf} links={links} />}

            <MonthStatementDialog
                isOpen={downloadOpen}
                onClose={() => setDownloadOpen(false)}
                scope={scope}
                initialMonth={monthOf(to)}
                subjects={subjects}
                initialSubject={subject}
                course={course}
                courses={courses}
                fixedSubject={Boolean(fixedSubject)}
            />
        </div>
    );
}

function ReportTiles({ report, locale }: { report: PractitionerReport; locale: string }) {
    const { t } = useTranslation();
    const count = (value: number) => formatCount(value, locale);
    const { totals } = report;
    const cancelled = totals.cancelled_by_patient + totals.cancelled_by_therapist;

    return (
        <div
            className={`grid grid-cols-1 gap-4 sm:grid-cols-2 ${report.has_amounts ? "lg:grid-cols-4" : "lg:grid-cols-3"}`}
        >
            <StatTile
                icon={CalendarCheck}
                label={t("reports.tiles.sessions")}
                value={count(totals.sessions)}
                caption={t("reports.tiles.sessionsCaption", {
                    finished: count(totals.finished),
                    pending: count(totals.pending),
                })}
            />
            <StatTile
                icon={CalendarX}
                label={t("reports.tiles.cancelled")}
                value={count(cancelled)}
                caption={t("reports.tiles.cancelledCaption", {
                    patient: count(totals.cancelled_by_patient),
                    therapist: count(totals.cancelled_by_therapist),
                })}
            />
            <StatTile
                icon={Clock}
                label={t("reports.tiles.hours")}
                value={(totals.minutes / 60).toLocaleString(locale, { maximumFractionDigits: 1 })}
                caption={`${t("reports.tiles.subjectsCaption", {
                    count: totals.subjects,
                    formatted: count(totals.subjects),
                })} · ${t("reports.tiles.attendeesCaption", {
                    count: totals.attendees,
                    formatted: count(totals.attendees),
                })}`}
            />
            {report.has_amounts && (
                <StatTile
                    icon={Euro}
                    label={t("reports.tiles.amount")}
                    value={formatMoney(toAmount(totals.amount ?? 0), locale)}
                    caption={t("reports.tiles.amountCaption")}
                />
            )}
        </div>
    );
}

function ReportCharts({
    report,
    granularity,
    locale,
    tokens,
}: {
    report: PractitionerReport;
    granularity: ReportGranularity;
    locale: string;
    tokens: ReturnType<typeof useChartTokens>;
}) {
    const { t } = useTranslation();
    const count = (value: number) => formatCount(value, locale);
    const money = (value: number) => formatMoney(value, locale);
    const categories = report.periods.map((p) => formatPeriodShort(p.period, granularity, locale));
    const fullLabels = report.periods.map((p) => formatPeriodFull(p.period, granularity, locale));
    const per = t(`center.reports.per.${granularity}`);
    const typeNames = report.by_type.map((row) => t(`reports.type.${row.type}`));
    const statuses = [
        { key: "finished", color: tokens.series1 },
        { key: "pending", color: tokens.series2 },
        { key: "cancelled", color: tokens.series3 },
    ] as const;

    return (
        <>
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                <ReportChartCard
                    className="lg:col-span-2"
                    title={t("reports.charts.sessionsOverTime")}
                    description={t("reports.charts.sessionsOverTimeHint", { per })}
                    empty={report.totals.sessions === 0}
                    table={{
                        columns: [
                            t("center.reports.period"),
                            ...statuses.map((s) => t(`reports.status.${s.key}`)),
                        ],
                        rows: report.periods.map((p, i) => [
                            fullLabels[i],
                            count(p.finished),
                            count(p.pending),
                            count(p.cancelled),
                        ]),
                    }}
                >
                    <TimeSeriesChart
                        kind="bar"
                        stacked
                        categories={categories}
                        fullLabels={fullLabels}
                        series={statuses.map((s) => ({
                            name: t(`reports.status.${s.key}`),
                            data: report.periods.map((p) => p[s.key]),
                            color: s.color,
                        }))}
                        tokens={tokens}
                        formatValue={count}
                        ariaLabel={t("reports.charts.sessionsOverTime")}
                    />
                </ReportChartCard>

                <ReportChartCard
                    title={t("reports.charts.byType")}
                    description={t("reports.charts.byTypeHint")}
                    empty={report.totals.sessions === 0}
                    table={{
                        columns: [t("reports.table.type"), t("reports.table.sessions")],
                        rows: report.by_type.map((row, i) => [typeNames[i], count(row.sessions)]),
                    }}
                >
                    <HorizontalBarChart
                        names={typeNames}
                        values={report.by_type.map((row) => row.sessions)}
                        tokens={tokens}
                        seriesName={t("reports.table.sessions")}
                        formatValue={count}
                        ariaLabel={t("reports.charts.byType")}
                    />
                </ReportChartCard>
            </div>

            {report.has_amounts && (
                <ReportChartCard
                    title={t("reports.charts.amountOverTime")}
                    description={t("reports.charts.amountOverTimeHint", { per })}
                    empty={toAmount(report.totals.amount ?? 0) === 0}
                    table={{
                        columns: [t("center.reports.period"), t("reports.table.amount")],
                        rows: report.periods.map((p, i) => [fullLabels[i], money(toAmount(p.amount ?? 0))]),
                    }}
                >
                    <TimeSeriesChart
                        kind="bar"
                        categories={categories}
                        fullLabels={fullLabels}
                        series={[
                            {
                                name: t("reports.table.amount"),
                                data: report.periods.map((p) => toAmount(p.amount ?? 0)),
                                color: tokens.series1,
                            },
                        ]}
                        tokens={tokens}
                        formatValue={money}
                        formatAxis={(value) => formatMoneyRounded(value, locale)}
                        ariaLabel={t("reports.charts.amountOverTime")}
                    />
                </ReportChartCard>
            )}
        </>
    );
}

function SubjectTable({
    report,
    locale,
    groupOf,
    links,
}: {
    report: PractitionerReport;
    locale: string;
    groupOf: (count: number) => string;
    links: ReportLinks;
}) {
    const { t } = useTranslation();
    const [typeFilter, setTypeFilter] = useState<SubjectTypeOption>("all");
    const { modes } = useSessionPreferences();
    const count = (value: number) => formatCount(value, locale);
    const withCourse = report.subjects.some((row) => row.course);
    const withCoverage = hasModuleCoverage(report.subjects);
    // Only a report across several professionals says whose each one is.
    const withMentor = report.mentors.length > 0;
    const counts: Record<SubjectTypeOption, number> = {
        all: report.subjects.length,
        patient: report.subjects.filter((row) => row.type === "patient").length,
        couple: report.subjects.filter((row) => row.type === "couple").length,
        group: report.subjects.filter((row) => row.type === "group").length,
    };
    const rows = useMemo(
        () => (typeFilter === "all" ? report.subjects : report.subjects.filter((row) => row.type === typeFilter)),
        [report.subjects, typeFilter]
    );
    const paged = usePagedRows(rows);
    const heading = "py-2 pr-4 font-medium";
    const cell = "py-2.5 pr-4 text-content-primary";
    const number = `${cell} text-right tabular-nums`;

    return (
        <section className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="text-base font-semibold text-content-primary">{t("reports.table.title")}</h2>
                {report.subjects.length > 0 && (
                    <SubjectTypeFilter value={typeFilter} onChange={setTypeFilter} counts={counts} kinds={offeredSubjectKinds(modes)} />
                )}
            </div>
            {rows.length === 0 ? (
                <p className="rounded-xl bg-surface-sunken p-6 text-center text-sm text-content-tertiary">
                    {report.subjects.length === 0 ? t("reports.empty") : t("reports.table.emptyType")}
                </p>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[640px] text-left text-sm">
                        <thead>
                            <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-content-tertiary dark:border-gray-800">
                                <th scope="col" className={heading}>{t("reports.table.subject")}</th>
                                {withCourse && <th scope="col" className={heading}>{t("reports.table.course")}</th>}
                                {withMentor && <th scope="col" className={heading}>{t("reports.table.mentor")}</th>}
                                {withCoverage && <ModuleCoverageHeading className={`${heading} text-right`} />}
                                <th scope="col" className={heading}>{t("reports.table.type")}</th>
                                <th scope="col" className={`${heading} text-right`}>{t("reports.table.sessions")}</th>
                                <th scope="col" className={`${heading} text-right`}>{t("reports.status.finished")}</th>
                                <th scope="col" className={`${heading} text-right`}>{t("reports.status.pending")}</th>
                                <th scope="col" className={`${heading} text-right`}>{t("reports.status.cancelled")}</th>
                                {report.has_amounts && (
                                    <th scope="col" className={`${heading} text-right`}>{t("reports.table.amount")}</th>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {paged.page.map((row) => (
                                <tr
                                    key={subjectKey(row.type, row.id)}
                                    className="border-b border-gray-100 last:border-0 dark:border-gray-800"
                                >
                                    <th scope="row" className={`${cell} font-medium`}>
                                        <MaybeLink to={links.subject(row.type, row.id)}>{subjectName(row, groupOf)}</MaybeLink>
                                    </th>
                                    {withCourse && <td className={cell}>{row.course ?? "—"}</td>}
                                    {withMentor && <td className={cell}>{row.mentors?.join(", ") || "—"}</td>}
                                    {withCoverage && <ModuleCoverageCell row={row} className={number} />}
                                    <td className={cell}>{t(`reports.type.${row.session_type}`)}</td>
                                    <td className={number}>{count(row.sessions)}</td>
                                    <td className={number}>{count(row.finished)}</td>
                                    <td className={number}>{count(row.pending)}</td>
                                    <td className={number}>{count(row.cancelled)}</td>
                                    {report.has_amounts && (
                                        <td className={number}>
                                            {row.amount === null
                                                ? t("reports.table.noRate")
                                                : formatMoney(toAmount(row.amount), locale)}
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            <Pagination
                offset={paged.offset}
                total={paged.total}
                pageSize={paged.pageSize}
                onPrev={paged.onPrev}
                onNext={paged.onNext}
            />
        </section>
    );
}
